import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { LUCIDE_ICONS } from '@compsych-ui-components/shared/assets/lucide-icons.generated';
import { RippleDirective } from './ripple.directive';

/**
 * Card type — selects the projected/laid-out content. Mirrors the library's `variant` pattern.
 * `basic` is the Figma "cards" section (the "Card Item" family: icon + arrow +
 * title/subtitle, sized, across the outlined/filled/gradient/image surfaces) —
 * distinct from the `content` category tiles.
 */
export type CardVariant = 'basic' | 'content' | 'action' | 'metric';
/** Visual surface treatment. Mirrors the Figma "Card Item" Style property. */
export type CardAppearance = 'outlined' | 'filled' | 'gradient' | 'image';
/** Corner radius token: `md` (12px), `lg` (16px), `xl` (24px). */
export type CardRadius = 'md' | 'lg' | 'xl';
/** Inner padding token (used by `variant="basic"`; typed variants size themselves). */
export type CardPadding = 'none' | 'sm' | 'md' | 'lg' | 'xl';
/** Action-card layout. */
export type CardOrientation = 'horizontal' | 'vertical';
/** Base Card Item size (Figma "Size" property). Drives layout + type scale. */
export type CardSize = 'xl' | 'lg' | 'md' | 'sm' | 'xs';
/** Heading level for the title / hero headline / action label (screen-reader heading semantics). */
export type CardHeadingLevel = 2 | 3 | 4 | 5 | 6;
/** How the content-variant category renders: trailing pill (`chip`) or leading plain-text `label`. */
export type CardCategoryStyle = 'chip' | 'label';
/**
 * Content-variant icon badge treatment:
 * - `primary` — white icon on a filled primary (blue) circle (default).
 * - `neutral` — dark (on-surface) icon on a white circle with a thin outline.
 */
export type CardIconStyle = 'primary' | 'neutral';

/**
 * Card — the single Cards component for the ComPsych Design System.
 *
 * Figma: Core Components — "Cards" (file VFBn7KCDy3FSIlvhNo3ylq, 1117:9829).
 *
 * One component, switched by `variant` (like Accordion's `variant`):
 * - `basic`   — a bare surface; project your own content.
 * - `content` — icon badge / image, category chip, title, subtitle, description,
 *               action pill, arrow; hero (`headline`) layout.
 * - `action`  — compact interactive row (icon · label/description · arrow); horizontal/vertical.
 * - `metric`  — centered value + label; `current` (selected) state.
 *
 * `appearance` is the orthogonal surface skin (outlined / filled / gradient / image).
 * When `interactive`, the whole card is a button (role/tabindex/keyboard) with the
 * hover / focus / pressed ripple. All values are sys design tokens.
 *
 *   <compsych-card variant="content" appearance="gradient" icon="circle-play"
 *     category="Video" title="Mindful breathing" actionLabel="Watch" [interactive]="true" />
 *   <compsych-card variant="metric" value="24" label="Sessions" [current]="true" [interactive]="true" />
 *   <compsych-card>Projected content</compsych-card>
 */
@Component({
  selector: 'compsych-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet],
  hostDirectives: [RippleDirective],
  templateUrl: './card.html',
  styleUrl: './card.css',
  host: {
    class: 'cmp-card',
    '[class.cmp-card--outlined]': "appearance() === 'outlined'",
    '[class.cmp-card--filled]': "appearance() === 'filled'",
    '[class.cmp-card--gradient]': "appearance() === 'gradient'",
    '[class.cmp-card--image]': "appearance() === 'image'",
    '[class.cmp-card--content]': "variant() === 'content'",
    '[class.cmp-card--basic]': "variant() === 'basic'",
    '[attr.data-card-size]': "variant() === 'basic' ? size() : null",
    '[class.cmp-card--hero]': "variant() === 'content' && !!headline()",
    '[class.cmp-card--metric]': "variant() === 'metric'",
    '[class.cmp-card--current]': "variant() === 'metric' && current()",
    '[class.cmp-card--radius-md]': "radius() === 'md'",
    '[class.cmp-card--radius-lg]': "radius() === 'lg'",
    '[class.cmp-card--radius-xl]': "radius() === 'xl'",
    '[class.cmp-card--pad-none]': "effectivePadding() === 'none'",
    '[class.cmp-card--pad-sm]': "effectivePadding() === 'sm'",
    '[class.cmp-card--pad-md]': "effectivePadding() === 'md'",
    '[class.cmp-card--pad-lg]': "effectivePadding() === 'lg'",
    '[class.cmp-card--pad-xl]': "effectivePadding() === 'xl'",
    '[style.width]': "fullWidth() ? '100%' : null",
    '[class.cmp-card--interactive]': 'interactive()',
    '[class.cmp-card--disabled]': 'disabled()',
    '[class.cmp-card--selected]': 'selected()',
    '[class.cmp-card--no-gradient]': '!showGradient()',
    '[class.cmp-card--compact]': 'compact()',
    '[attr.role]': "interactive() ? 'button' : null",
    '[attr.tabindex]': 'interactive() && !disabled() ? 0 : null',
    '[attr.aria-label]': 'ariaLabel() ?? null',
    '[attr.aria-disabled]': 'disabled() || null',
    '[attr.aria-pressed]': "interactive() && variant() === 'metric' ? current() : null",
    '[style.border-color]': "variant() === 'metric' && current() ? 'var(--accent-primary-sys-primary)' : null",
    '[style.border-width]': "variant() === 'metric' && current() ? 'var(--border-width-sys-stroke-thick)' : null",
    '(click)': 'onActivate()',
    '(keydown)': 'onKeyDown($event)',
  },
})
export class CardComponent {
  // ── Surface (all variants) ──────────────────────────────
  /** Card type. Defaults to `basic` (projected content). */
  readonly variant = input<CardVariant>('basic');
  /** Surface treatment. Defaults to `outlined`. */
  readonly appearance = input<CardAppearance>('outlined');
  /** Corner radius token. Defaults to `lg` (16px). */
  readonly radius = input<CardRadius>('lg');
  /** Inner padding for `variant="basic"`. Typed variants size themselves. */
  readonly padding = input<CardPadding>('lg');
  /** Card Item size (`variant="basic"`): xl/lg/md/sm/xs. Defaults to `md`. */
  readonly size = input<CardSize>('md');
  /** Whole-card click target: adds role/tabindex, hover/press states and ripple. */
  readonly interactive = input<boolean>(false);
  /** Disabled visual + non-interactive. */
  readonly disabled = input<boolean>(false);
  /** Applies the focused (blue outline) appearance. Useful for action cards to indicate selection. */
  readonly selected = input<boolean>(false);
  /** Background image URL for `appearance="image"`. */
  readonly imageSrc = input<string>('');
  /**
   * `background-position` for the `image` appearance media (e.g. `'85% 0'` for an
   * off-center hero). Defaults to `'center'` — unchanged from before.
   */
  readonly imagePosition = input<string>('center');

  // ── Content variant ─────────────────────────────────────
  /** Lucide icon name (content badge / action leading icon). */
  readonly icon = input<string>('');
  /** Content: category chip text. */
  readonly category = input<string>('');
  /**
   * Content: how the category renders — `'chip'` (default) as the trailing pill,
   * or `'label'` as a leading plain-text label beside the icon.
   */
  readonly categoryStyle = input<CardCategoryStyle>('chip');
  /**
   * Content: icon badge treatment — `'primary'` (default) is a white icon on a
   * filled primary (blue) circle; `'neutral'` is a dark icon on a white circle
   * with a thin outline.
   */
  readonly iconStyle = input<CardIconStyle>('primary');
  /** Content: title. */
  readonly title = input<string>('');
  /** Content: subtitle. */
  readonly subtitle = input<string>('');
  /** Content/action: supporting description. */
  readonly description = input<string>('');
  /** Content: large hero display text (switches to the XL hero layout). */
  readonly headline = input<string>('');
  /** Content: presentational action pill label. */
  readonly actionLabel = input<string>('');
  /** Content: show the trailing arrow-up-right affordance. */
  readonly showArrow = input<boolean>(true);
  /** Content (gradient): show the inner gradient panel background. Set to `false` to render a flat surface. */
  readonly showGradient = input<boolean>(true);
  /** Content: remove the fixed min-height so the card sizes to its content. */
  readonly compact = input<boolean>(false);

  // ── Action variant ──────────────────────────────────────
  /** Action/metric: primary label. */
  readonly label = input<string>('');
  /** Action: trailing Lucide icon (horizontal layout). */
  readonly trailingIcon = input<string>('arrow-up-right');
  /** Action: layout orientation. */
  readonly orientation = input<CardOrientation>('horizontal');
  /** Action: stretch the card to 100% of its container width. */
  readonly fullWidth = input<boolean>(false);

  // ── Metric variant ──────────────────────────────────────
  /** Metric: the prominent value. */
  readonly value = input<string | number>('');
  /** Metric: selected/current state (primary border + text). */
  readonly current = input<boolean>(false);

  // ── Accessibility ───────────────────────────────────────
  /**
   * Opts the title / hero headline / action label into a heading in the
   * accessibility tree via `role="heading"` + `aria-level`, so consumers that
   * replace real headings keep heading semantics and screen-reader heading
   * navigation (WCAG 1.3.1). Omit (default) to render as plain text — no visual
   * or behavioral change for existing usages.
   */
  readonly headingLevel = input<CardHeadingLevel | undefined>(undefined);
  /**
   * Accessible name for an `interactive` card (which is a `role="button"`).
   * Required for image-only / icon-only interactive cards that have no text, and
   * useful to give text-heavy cards a concise name instead of their full content.
   * Bind as `aria-label`.
   */
  readonly ariaLabel = input<string | undefined>(undefined, { alias: 'aria-label' });

  /** Emits on click / Enter / Space when interactive and not disabled. */
  readonly cardClick = output<void>();

  private readonly sanitizer = inject(DomSanitizer);
  private readonly ripple = inject(RippleDirective);

  readonly isImage = computed(() => this.appearance() === 'image');

  /**
   * Heading level actually applied. Suppressed when the whole card is interactive
   * (`role="button"`), since a heading nested in a button isn't exposed as a
   * heading — the two would conflict.
   */
  readonly effectiveHeadingLevel = computed(() =>
    this.interactive() ? undefined : this.headingLevel(),
  );

  /** Base Card Item plain-icon glyph size by `size` (48 md, 32 sm, 24 xs). */
  readonly basicIconSize = computed(() => (this.size() === 'sm' ? 32 : this.size() === 'xs' ? 24 : 48));
  /** Base Card Item arrow glyph size by `size` (24 md, 20 sm/xs). */
  readonly basicArrowSize = computed(() => (this.size() === 'md' ? 24 : 20));

  /** CSS `url(...)` for the media layer, or null when no image is set. */
  readonly mediaUrl = computed(() => {
    const src = this.imageSrc();
    return src ? `url("${src}")` : null;
  });

  /** Padding resolved per variant; `basic` honors the `padding` input. */
  readonly effectivePadding = computed<CardPadding>(() => {
    switch (this.variant()) {
      case 'content':
        return this.isImage() || this.headline() ? 'none' : 'sm';
      case 'action':
        return 'lg'; // 24px — Figma action card (60017:8036)
      case 'basic':
        return 'none'; // .bc controls its own padding per style (Figma Base Card Item)
      case 'metric':
        return 'lg'; // 24px — Figma metric card (60024:10073)
      default:
        return this.padding();
    }
  });

  constructor() {
    effect(() => {
      this.ripple.suppressed = !this.interactive() || this.disabled();
    });
  }

  onActivate(): void {
    if (!this.interactive() || this.disabled()) return;
    this.cardClick.emit();
  }

  onKeyDown(event: KeyboardEvent): void {
    if (!this.interactive() || this.disabled()) return;
    if (event.repeat) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.cardClick.emit();
  }

  /** Renders a sized/weighted Lucide SVG for template use. */
  iconSvg(name: string, size: number, strokeWidth = 1.75): SafeHtml {
    let svg = LUCIDE_ICONS[name] ?? '';
    svg = svg.replace('<svg ', `<svg width="${size}" height="${size}" `);
    if (strokeWidth !== 1.5) {
      svg = svg.replace('stroke-width="1.5"', `stroke-width="${strokeWidth}"`);
    }
    return this.sanitizer.bypassSecurityTrustHtml(svg);
  }
}
